import { atom, getDefaultStore } from 'jotai';

import type { CalendarModule } from '../types/calendarEvent';
import type { CalendarEventSource } from '../types/calendarEvent';
import type { ColorMode } from '../types/colorConfig';

import { patchScopedSettingValuesMutationAtom } from '~/modules/settings/_graphql/mutationPatchScopedSettingValues';
import { $user } from '~/shared/providers/auth/identity.localState';
import {
  $allSettingsGroups,
  $userCalendarColorMode,
  $userCalendarDefaultView,
  $userCalendarEnabledLayers,
} from '~/shared/state/settings.state';

export type EnabledLayers = Partial<Record<CalendarEventSource, boolean>>;

const DEFAULT_COLOR_MODE: ColorMode = 'byModule';
const DEFAULT_VIEW = 'dayGridMonth';

const DEFAULT_ENABLED_LAYERS: EnabledLayers = {
  booking: true,
  load: true,
  salesOrder: true,
  route: true,
  driverCertification: true,
  workOrder: true,
  inboundTolling: true,
  outboundTolling: true,
  dispatchRecord: false,
  invoice: false,
  bill: false,
  inventoryMovement: false,
};

export const MODULE_SOURCES: Record<CalendarModule, CalendarEventSource[]> = {
  trade: ['booking', 'load', 'salesOrder'],
  transportation: ['route', 'driverCertification'],
  production: ['workOrder', 'inboundTolling', 'outboundTolling'],
  compliance: ['dispatchRecord'],
  finance: ['invoice', 'bill'],
  inventory: ['inventoryMovement'],
};

const ALL_SOURCES: CalendarEventSource[] = Object.values(MODULE_SOURCES).flat();

function migrateModuleLayers(stored: EnabledLayers): EnabledLayers {
  const hasAnySourceKey = ALL_SOURCES.some((s) => s in stored);
  if (hasAnySourceKey) return stored;

  const migrated: EnabledLayers = {};
  for (const [mod, sources] of Object.entries(MODULE_SOURCES)) {
    const enabled = (stored as Record<string, boolean>)[mod] ?? false;
    for (const source of sources) {
      migrated[source] = enabled;
    }
  }
  return migrated;
}

const $savePreference = patchScopedSettingValuesMutationAtom({
  onSuccess: (get) => {
    get($allSettingsGroups).refetch?.();
  },
});

const $colorModeOverride = atom<ColorMode | null>(null);
const $enabledLayersOverride = atom<EnabledLayers | null>(null);
const $defaultViewOverride = atom<string | null>(null);

let debounceTimer: ReturnType<typeof setTimeout> | null = null;

const debouncedSave = () => {
  if (debounceTimer) clearTimeout(debounceTimer);
  debounceTimer = setTimeout(async () => {
    const store = getDefaultStore();
    const user = store.get($user);
    if (!user) return;

    const colorMode = store.get($colorMode);
    const defaultView = store.get($defaultView);
    const enabledLayers = store.get($enabledLayers);
    try {
      await store.set($savePreference, {
        scope: 'User',
        objectId: user.id,
        values: {
          'calendar.colorMode': colorMode,
          'calendar.defaultView': defaultView,
          'calendar.enabledLayers': enabledLayers,
        },
      });
    } catch {
      // Mutation failed; overrides remain as local source of truth
    }
  }, 300);
};

export const $colorMode = atom(
  (get): ColorMode => {
    const override = get($colorModeOverride);
    if (override !== null) return override;
    return (get($userCalendarColorMode) as ColorMode) ?? DEFAULT_COLOR_MODE;
  },
  (_get, set, value: ColorMode) => {
    set($colorModeOverride, value);
    debouncedSave();
  },
);

export const $enabledLayers = atom(
  (get): EnabledLayers => {
    const override = get($enabledLayersOverride);
    if (override !== null) return override;
    const stored = get($userCalendarEnabledLayers) as EnabledLayers | undefined;
    if (!stored) return DEFAULT_ENABLED_LAYERS;
    return migrateModuleLayers(stored);
  },
  (_get, set, value: EnabledLayers) => {
    set($enabledLayersOverride, value);
    debouncedSave();
  },
);

export const $defaultView = atom(
  (get): string => {
    const override = get($defaultViewOverride);
    if (override !== null) return override;
    return get($userCalendarDefaultView) ?? DEFAULT_VIEW;
  },
  (_get, set, value: string) => {
    set($defaultViewOverride, value);
    debouncedSave();
  },
);
